/**
 * @file: getIssueLevelCls.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  AnyItemWithDefectiveInfo,
  DefectiveLevel,
  DefectiveLevelText,
  DefectiveLevelTextShort,
  DefectiveLevelTokenMap,
} from '@euler/model';
import {
  AbnormalLevel,
  DefectiveLevel as DefectiveLevelEnum,
  SeverityLevel,
} from '@euler/model/enum';
import Color from 'color';
import { DefaultTheme } from 'styled-components';

export function getDefectiveLevelFrom(obj: AnyItemWithDefectiveInfo) {
  return getDefectiveLevel(obj.abnormalLevel, obj.severityLevel);
}

export function getDefectiveLevel(
  abnormalLevel: AbnormalLevel,
  severityLevel: SeverityLevel,
): DefectiveLevel {
  if (abnormalLevel === AbnormalLevel.Fine) {
    return DefectiveLevel.Fine;
  }

  if (
    abnormalLevel === AbnormalLevel.Urgent ||
    severityLevel === SeverityLevel.Danger
  ) {
    return DefectiveLevel.Urgent;
  }

  if (severityLevel === SeverityLevel.Warning) {
    return DefectiveLevel.Warning;
  }

  return DefectiveLevel.Defective;
}

export function getDefectiveLevelTokenByValue(value: DefectiveLevel) {
  return DefectiveLevelTokenMap[value];
}

export const defectiveLevelFromEnum = (value: DefectiveLevelEnum) => {
  switch (value) {
    case DefectiveLevelEnum.Urgent:
      return DefectiveLevel.Urgent;
    case DefectiveLevelEnum.Warning:
      return DefectiveLevel.Warning;
    case DefectiveLevelEnum.Slight:
      return DefectiveLevel.Defective;
    default:
      return DefectiveLevel.Fine;
  }
};

export function getDefectiveLevelToken(
  abnormalLevel: AbnormalLevel,
  severityLevel: SeverityLevel,
) {
  const level = getDefectiveLevel(abnormalLevel, severityLevel);
  return DefectiveLevelTokenMap[level];
}

export function getDefectiveLevelColor(
  theme: DefaultTheme,
  level: DefectiveLevel,
): string {
  const token = DefectiveLevelTokenMap[level];
  return (theme.colors.defectiveLevel as any)[token];
}

export function getDefectiveLevelBgColor(
  theme: DefaultTheme,
  level: DefectiveLevel,
  opacity = 0.1,
): string {
  const color = getDefectiveLevelColor(theme, level);
  return Color(color).alpha(opacity).string();
}

export function getDefectiveLevelText(level: DefectiveLevel) {
  return DefectiveLevelText[level];
}

export function getDefectiveLevelTextShort(level: DefectiveLevel) {
  return DefectiveLevelTextShort[level];
}

export function defectiveLevelDistributed(level: DefectiveLevel): {
  abnormalLevel: AbnormalLevel;
  severityLevel: SeverityLevel;
} {
  switch (level) {
    case DefectiveLevel.Fine: {
      return {
        abnormalLevel: AbnormalLevel.Fine,
        severityLevel: SeverityLevel.None,
      };
    }
    case DefectiveLevel.Urgent: {
      return {
        abnormalLevel: AbnormalLevel.Urgent,
        severityLevel: SeverityLevel.Danger,
      };
    }
    case DefectiveLevel.Warning: {
      return {
        abnormalLevel: AbnormalLevel.Defective,
        severityLevel: SeverityLevel.Warning,
      };
    }
    default: {
      return {
        abnormalLevel: AbnormalLevel.Defective,
        severityLevel: SeverityLevel.Notice,
      };
    }
  }
}
